import type {
  PromoCodeRedeemResponse,
  SpinWheelClaimDailyCreditResponse,
  SpinWheelSpinResponse,
  SpinWheelStatusResponse,
  SpinWheelWildRedeemResponse,
  SpinWheelWildRedemptionItem,
  SpinWheelWildRewardItem,
  SpinWheelWildStatusResponse,
} from '@spanel/shared-types';

import { sendAction } from './api';
import { parseLocalizedAmount } from '../utils/amount';
import { resolveSiteAssetUrl } from '../utils/siteAssetUrl';

export type SpinWheelSegment = {
  id: string;
  label: string;
  weight: number;
  bonusId: number;
  bonusName: string;
  color: string;
  active: boolean;
  rewardType?: 'bonus' | 'physical' | 'wild';
  physicalPrizeNote?: string;
  wildAmount?: number;
};

export type SpinWheelWildReward = {
  id: string;
  label: string;
  wildCost: number;
  rewardType: 'bonus' | 'physical';
  bonusId: number;
  bonusName: string;
  physicalPrizeNote: string;
  imageUrl: string;
  description: string;
  stock: number | null;
  sortOrder: number;
  active: boolean;
};

export type SpinWheelWildRedemption = {
  id: string;
  rewardId: string;
  rewardLabel: string;
  rewardType: 'bonus' | 'physical';
  wildCost: number;
  status: 'pending' | 'fulfilled' | 'rejected';
  bonusId: number;
  bonusName: string;
  physicalPrizeNote: string;
  createdAt: string | null;
  fulfilledAt: string | null;
};

export type SpinWheelWildStatus = {
  balance: number;
  catalog: SpinWheelWildReward[];
  recentRedemptions: SpinWheelWildRedemption[];
};

export type SpinWheelWildRedeemResult = {
  message: string;
  balance: number;
  redemption: SpinWheelWildRedemption | null;
  idempotentReplay: boolean;
};

export type SpinWheelStatus = {
  enabled: boolean;
  popupAutoOpen: boolean;
  availableCredits: number;
  progress: {
    depositCount: number;
    depositAmount: number;
    requiredDepositCount: number;
    requiredDepositAmount: number;
    goalMet: boolean;
    dailyCreditClaimed: boolean;
    dailyCreditClaimable: boolean;
  };
  segments: SpinWheelSegment[];
};

export type SpinWheelSpinResult = {
  message: string;
  availableCredits: number;
  segment: SpinWheelSegment | null;
  spinId: string | null;
  wildBalanceAfter?: number;
};

export type SpinWheelClaimDailyCreditResult = {
  message: string;
  creditsAdded: number;
  availableCredits: number;
};

const RETRY_SEGMENT_ID = 'retry-segment';

function readNonNegativeNumber(value: unknown): number | null {
  const parsed = parseLocalizedAmount(value);
  return parsed !== null && parsed >= 0 ? parsed : null;
}

function readNonNegativeInteger(value: unknown): number | null {
  const parsed = readNonNegativeNumber(value);
  return parsed !== null && Number.isInteger(parsed) ? parsed : null;
}

function normalizeSegment(payload: unknown): SpinWheelSegment | null {
  const item =
    payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
  const label = typeof item.label === 'string' ? item.label : '';
  const weight = parseLocalizedAmount(item.weight);
  const id = typeof item.id === 'string' ? item.id : '';
  if (!id || !label || weight === null || weight <= 0 || typeof item.active !== 'boolean') {
    return null;
  }
  if (item.reward_type === 'physical') {
    return {
      id,
      label,
      weight,
      bonusId: 0,
      bonusName: '',
      color: typeof item.color === 'string' && item.color ? item.color : '#ff6b35',
      active: item.active !== false,
      rewardType: 'physical',
      physicalPrizeNote:
        typeof item.physical_prize_note === 'string' ? item.physical_prize_note : '',
    };
  }
  if (item.reward_type === 'wild') {
    const wildAmountRaw = readNonNegativeInteger(item.wild_amount);
    if (wildAmountRaw === null || wildAmountRaw <= 0) {
      return null;
    }
    return {
      id,
      label,
      weight,
      bonusId: 0,
      bonusName: '',
      color: typeof item.color === 'string' && item.color ? item.color : '#ff6b35',
      active: item.active !== false,
      rewardType: 'wild',
      wildAmount: wildAmountRaw,
    };
  }
  if (id === RETRY_SEGMENT_ID) {
    return {
      id,
      label,
      weight,
      bonusId: 0,
      bonusName: '',
      color: typeof item.color === 'string' && item.color ? item.color : '#ff6b35',
      active: item.active !== false,
    };
  }
  const bonusId = readNonNegativeInteger(item.bonus_id);
  if (bonusId === null || bonusId <= 0) {
    return null;
  }

  return {
    id,
    label,
    weight,
    bonusId,
    bonusName: typeof item.bonus_name === 'string' ? item.bonus_name : '',
    color: typeof item.color === 'string' && item.color ? item.color : '#ff6b35',
    active: item.active !== false,
    rewardType: 'bonus',
  };
}

function normalizeStatus(response: SpinWheelStatusResponse): SpinWheelStatus {
  const progress = response.progress;
  if (
    typeof response.enabled !== 'boolean' ||
    typeof response.popup_auto_open !== 'boolean' ||
    !progress ||
    typeof progress !== 'object' ||
    !Array.isArray(response.segments)
  ) {
    throw new Error('Çark durumu doğrulanamadı.');
  }

  const availableCredits = readNonNegativeInteger(response.available_credits);
  const depositCount = readNonNegativeInteger(progress.deposit_count);
  const depositAmount = readNonNegativeNumber(progress.deposit_amount);
  const requiredDepositCount = readNonNegativeInteger(progress.required_deposit_count);
  const requiredDepositAmount = readNonNegativeNumber(progress.required_deposit_amount);
  if (
    availableCredits === null ||
    depositCount === null ||
    depositAmount === null ||
    requiredDepositCount === null ||
    requiredDepositAmount === null ||
    typeof progress.goal_met !== 'boolean' ||
    typeof progress.daily_credit_claimed !== 'boolean' ||
    typeof progress.daily_credit_claimable !== 'boolean'
  ) {
    throw new Error('Çark durumu doğrulanamadı.');
  }

  return {
    enabled: response.enabled,
    popupAutoOpen: response.popup_auto_open,
    availableCredits,
    progress: {
      depositCount,
      depositAmount,
      requiredDepositCount,
      requiredDepositAmount,
      goalMet: progress.goal_met,
      dailyCreditClaimed: progress.daily_credit_claimed,
      dailyCreditClaimable: progress.daily_credit_claimable,
    },
    segments: response.segments
      .map((segment) => normalizeSegment(segment))
      .filter(Boolean) as SpinWheelSegment[],
  };
}

function normalizeWildReward(item: SpinWheelWildRewardItem): SpinWheelWildReward | null {
  const raw = item as unknown as Record<string, unknown>;
  const id = String(raw.id ?? '').trim();
  const label = typeof raw.label === 'string' ? raw.label.trim() : '';
  const wildCost = readNonNegativeInteger(raw.wild_cost);
  const rewardType = raw.reward_type;
  const bonusId = readNonNegativeInteger(raw.bonus_id);
  const stockRaw = raw.stock;
  const stockNumber = stockRaw === null ? null : readNonNegativeInteger(stockRaw);
  const hasValidStock = stockRaw === null || stockNumber !== null;

  if (
    !id ||
    !label ||
    wildCost === null ||
    wildCost <= 0 ||
    (rewardType !== 'bonus' && rewardType !== 'physical') ||
    !hasValidStock ||
    (rewardType === 'bonus' && (bonusId === null || bonusId <= 0))
  ) {
    return null;
  }

  const stock = stockRaw === null ? null : stockNumber;
  return {
    id,
    label,
    wildCost,
    rewardType,
    bonusId: rewardType === 'bonus' ? bonusId! : 0,
    bonusName: typeof raw.bonus_name === 'string' ? raw.bonus_name : '',
    physicalPrizeNote:
      typeof raw.physical_prize_note === 'string' ? raw.physical_prize_note : '',
    imageUrl: typeof raw.image_url === 'string' ? resolveSiteAssetUrl(raw.image_url) : '',
    description: typeof raw.description === 'string' ? raw.description : '',
    stock,
    sortOrder: Number.isFinite(Number(raw.sort_order)) ? Number(raw.sort_order) : 0,
    active: raw.active === true,
  };
}

function normalizeWildRedemption(item: SpinWheelWildRedemptionItem): SpinWheelWildRedemption | null {
  const raw = item as unknown as Record<string, unknown>;
  const id = String(raw.id ?? '').trim();
  const rewardId = typeof raw.reward_id === 'string' ? raw.reward_id.trim() : '';
  const rewardLabel = typeof raw.reward_label === 'string' ? raw.reward_label.trim() : '';
  const rewardType = raw.reward_type;
  const wildCost = readNonNegativeInteger(raw.wild_cost);
  const status = raw.status;
  const bonusId = readNonNegativeInteger(raw.bonus_id);
  if (
    !id ||
    !rewardId ||
    !rewardLabel ||
    (rewardType !== 'bonus' && rewardType !== 'physical') ||
    wildCost === null ||
    wildCost <= 0 ||
    (status !== 'pending' && status !== 'fulfilled' && status !== 'rejected') ||
    (rewardType === 'bonus' && (bonusId === null || bonusId <= 0))
  ) {
    return null;
  }
  return {
    id,
    rewardId,
    rewardLabel,
    rewardType,
    wildCost,
    status,
    bonusId: rewardType === 'bonus' ? bonusId! : 0,
    bonusName: typeof raw.bonus_name === 'string' ? raw.bonus_name : '',
    physicalPrizeNote:
      typeof raw.physical_prize_note === 'string' ? raw.physical_prize_note : '',
    createdAt: typeof raw.created_at === 'string' ? raw.created_at : null,
    fulfilledAt: typeof raw.fulfilled_at === 'string' ? raw.fulfilled_at : null,
  };
}

function normalizeWildStatus(response: SpinWheelWildStatusResponse): SpinWheelWildStatus {
  const balance = readNonNegativeInteger(response.balance);
  if (balance === null || !Array.isArray(response.catalog) || !Array.isArray(response.recent_redemptions)) {
    throw new Error('Wild ödül durumu doğrulanamadı.');
  }
  return {
    balance,
    catalog: response.catalog
      .map((item) => normalizeWildReward(item))
      .filter((item): item is SpinWheelWildReward => item !== null && item.active),
    recentRedemptions: response.recent_redemptions
      .map((item) => normalizeWildRedemption(item))
      .filter((item): item is SpinWheelWildRedemption => item !== null),
  };
}

function normalizeWildRedeemResult(response: SpinWheelWildRedeemResponse): SpinWheelWildRedeemResult {
  const balance = readNonNegativeInteger(response.balance);
  const redemption = response.redemption ? normalizeWildRedemption(response.redemption) : null;
  if (balance === null || !redemption) {
    throw new Error('Wild ödül sonucu doğrulanamadı.');
  }
  return {
    message: typeof response.message === 'string' ? response.message : '',
    balance,
    redemption,
    idempotentReplay: response.idempotent_replay === true,
  };
}

function normalizeSpinResult(response: SpinWheelSpinResponse): SpinWheelSpinResult {
  const raw = response as unknown as Record<string, unknown>;
  const availableCredits = readNonNegativeInteger(response.available_credits);
  const segment = response.segment ? normalizeSegment(response.segment) : null;
  const hasWildBalanceAfter = raw.wild_balance_after !== null && raw.wild_balance_after !== undefined;
  const wildBalanceAfter = hasWildBalanceAfter
    ? readNonNegativeInteger(raw.wild_balance_after)
    : null;
  if (
    availableCredits === null ||
    (response.segment != null && !segment) ||
    (hasWildBalanceAfter && wildBalanceAfter === null)
  ) {
    throw new Error('Çark sonucu doğrulanamadı.');
  }
  return {
    message: typeof response.message === 'string' ? response.message : '',
    availableCredits,
    segment,
    spinId: typeof response.spin_id === 'string' ? response.spin_id : null,
    ...(wildBalanceAfter !== null ? { wildBalanceAfter } : {}),
  };
}

export const spinWheelService = {
  getStatus: async (): Promise<SpinWheelStatus> => {
    const response = await sendAction<SpinWheelStatusResponse>('spinWheelStatus');
    return normalizeStatus(response);
  },
  spin: async (): Promise<SpinWheelSpinResult> => {
    const response = await sendAction<SpinWheelSpinResponse>('spinWheelSpin');
    return normalizeSpinResult(response);
  },
  redeemCode: async (code: string): Promise<PromoCodeRedeemResponse> => {
    return sendAction<PromoCodeRedeemResponse>('promoCodeRedeem', { code });
  },
  claimDailyCredit: async (): Promise<SpinWheelClaimDailyCreditResult> => {
    const response = await sendAction<SpinWheelClaimDailyCreditResponse>(
      'spinWheelClaimDailyCredit'
    );
    const creditsAdded = readNonNegativeInteger(response.credits_added);
    const availableCredits = readNonNegativeInteger(response.available_credits);
    if (creditsAdded === null || availableCredits === null) {
      throw new Error('Çark hakkı sonucu doğrulanamadı.');
    }
    return {
      message: typeof response.message === 'string' ? response.message : '',
      creditsAdded,
      availableCredits,
    };
  },
  getWildStatus: async (): Promise<SpinWheelWildStatus> => {
    const response = await sendAction<SpinWheelWildStatusResponse>('spinWheelWildStatus');
    return normalizeWildStatus(response);
  },
  redeemWild: async (
    rewardId: string,
    idempotencyKey?: string
  ): Promise<SpinWheelWildRedeemResult> => {
    const response = await sendAction<SpinWheelWildRedeemResponse>('spinWheelWildRedeem', {
      reward_id: rewardId,
      ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
    });
    return normalizeWildRedeemResult(response);
  },
};
